import { Component, DestroyRef, effect, inject, Injector, input, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TooltipIconComponent } from '../../../common/components/base-settings-dialog/tooltip-icon/tooltip-icon.component';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { OrgAdminEndpointsService } from '../../../services/org-service/org-admin-endpoints.service';
import { catchError, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { OrgAdminServerConfigTestIds } from './org-admin-server-config.test-ids';

const AVG_DAYS_PER_MONTH = 365.2425 / 12; // 30.436875
const SECONDS_PER_DAY = 86400;
const SECONDS_PER_MONTH = AVG_DAYS_PER_MONTH * SECONDS_PER_DAY; // 2_629_746

@Component({
  selector: 'app-org-admin-server-config',
  imports: [ReactiveFormsModule, TooltipIconComponent, MatSlideToggle, FormsModule, ButtonComponent],
  templateUrl: './org-admin-server-config.component.html',
  styleUrl: './org-admin-server-config.component.scss'
})
/**
 * Begründung: Hier wird die Löschfrist konfiguration verwaltet und nicht direkt die Löschung, daher keine AFO
 */
export class OrgAdminServerConfigComponent implements OnInit {
  protected readonly testIds = OrgAdminServerConfigTestIds;
  domain = input.required<string>();
  lastSavedFormValue:
    | {
        isActive: boolean | null;
        deletionPeriod: number | null;
      }
    | undefined;
  private readonly orgAdminEnpointService = inject(OrgAdminEndpointsService);
  private readonly injector = inject(Injector);
  private readonly formBuilder = inject(FormBuilder);
  deletionPeriodForm = this.formBuilder.group({
    isActive: [true],
    deletionPeriod: [0]
  });
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    effect(
      () => {
        this.loadDeletionPeriod();
      },
      { injector: this.injector }
    );
  }
  loadDeletionPeriod(): void {
    //TODO: SOllte evtl mal durch den Aufruf direkt an der Löschfrist ersetzt zu werden
    this.orgAdminEnpointService
      .getMessengerService(this.domain())
      .pipe(
        tap(resp => {
          if (resp.result.loeschFrist < 0) {
            this.deletionPeriodForm.controls['isActive'].setValue(false);
            this.deletionPeriodForm.controls['deletionPeriod'].disable();
          } else {
            this.deletionPeriodForm.controls['isActive'].setValue(true);
            this.deletionPeriodForm.controls['deletionPeriod'].enable();
          }
        })
      )
      .subscribe(resp => {
        this.deletionPeriodForm.controls['deletionPeriod'].setValue(this.convertToMonth(resp.result.loeschFrist));
        this.lastSavedFormValue = this.deletionPeriodForm.getRawValue();
      });
  }
  convertToMonth(deletePeriod: number): number {
    return Math.round(deletePeriod / SECONDS_PER_MONTH);
  }

  convertToSeconds(deletePeriodMonths: number): number {
    return deletePeriodMonths * SECONDS_PER_MONTH;
  }

  saveDeletionPeriod(): void {
    const deletePeriodMonths = this.deletionPeriodForm.value.deletionPeriod;
    if (this.deletionPeriodForm.value.isActive && deletePeriodMonths && deletePeriodMonths >= 0) {
      const deletionPeriodInSec = this.convertToSeconds(deletePeriodMonths);
      this.orgAdminEnpointService.patchDeletionPeriod(deletionPeriodInSec, this.domain()).subscribe();
    } else if (!this.deletionPeriodForm.value.isActive) {
      this.orgAdminEnpointService
        .patchDeletionPeriod(-1, this.domain())
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.snackbarService.error('Die Löschfrist konnte nicht gespeichert werden');
            throw new Error('Could not save deletionperiod');
          })
        )
        .subscribe(resp => {
          this.deletionPeriodForm.controls['deletionPeriod'].setValue(this.convertToMonth(resp.result.loeschFrist));
          this.snackbarService.success('Die Löschfrist wurde erfolgreich gespeichert');
        });
    }
  }

  onSliderChange(event: MatSlideToggleChange): void {
    if (event.checked) {
      this.deletionPeriodForm.controls['deletionPeriod'].enable();
      this.deletionPeriodForm.controls['isActive'].setValue(true);
    } else {
      this.deletionPeriodForm.controls['isActive'].setValue(false);
      this.deletionPeriodForm.controls['deletionPeriod'].disable();
    }
  }

  resetForm(): void {
    if (this.lastSavedFormValue) {
      this.deletionPeriodForm.patchValue(this.lastSavedFormValue);
      if (this.lastSavedFormValue.isActive) {
        this.deletionPeriodForm.controls['deletionPeriod'].enable();
      }
    } else {
      throw new Error('The last saved values where undefined');
    }
  }
}
